import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  ConnectionAuthModel,
  ConnectionParams,
  EndpointModel,
  EndpointType,
  RemoteEndpointsModel,
  RemoteServiceType,
} from '@libs/shared/models';
import { catchError, Observable, switchMap, timeout } from 'rxjs';
import { CapacitorService } from '../capacitor';
import { EnvironmentService } from '../environment';
import { CmsLanguageService } from '../language';
import { ConnectorGlobalErrorHandlerService } from './connector-global-error-handler';
import { QueryParameterFormatterService } from './query-parameter-formmater';

@Injectable({ providedIn: 'root' })
export class ConnectorBaseService {
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);
  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly queryParameterFormatterService: QueryParameterFormatterService = inject(QueryParameterFormatterService);
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly globalErrorHandlerService: ConnectorGlobalErrorHandlerService = inject(ConnectorGlobalErrorHandlerService);

  public connect<
    T, S extends RemoteServiceType = RemoteServiceType, E extends EndpointType<S> = EndpointType<S>,
  >(service: S, endpoint: E, auth?: ConnectionAuthModel, params?: ConnectionParams): Observable<T> {
    return this.env.bff().pipe(
      switchMap((bff) => {
        const serviceConfig: RemoteEndpointsModel<S> = bff.services[service];
        const endpointConfig: EndpointModel = serviceConfig[endpoint];

        const formattedPath: string = this.formatPathParams(endpointConfig.path, params?.pathParams);
        const formattedQueryParams: Params | undefined = this.queryParameterFormatterService.formatQueryParams(params?.searchParams);
        const headers: Record<string, string> = this.formatHeaders(auth, params);

        return this.http.request<T>(String(endpointConfig.method), `${bff.url}${formattedPath}`, {
          body: params?.payload,
          params: formattedQueryParams,
          headers: headers,
          withCredentials: false,
          // Angular's typings tie request<T> to the literal 'body', but the runtime returns the requested type
          observe: params?.observe as 'body',
          // Angular's typings tie request<T> to the literal 'json', but the runtime returns the requested type
          responseType: (params?.responseType ?? 'json') as 'json',
        }).pipe(
          timeout(60000),
          catchError((response: HttpErrorResponse) =>
            this.globalErrorHandlerService
              .handleError<T, S, E>(response, service, endpoint, this.connect<T, S, E>(service, endpoint, auth, params)),
          ),
        );
      }),
    );
  }

  private formatPathParams(path: string, pathParams?: Record<string, string>): string {
    if (!pathParams) {
      return path;
    }

    const pathParamRegExp: RegExp = /:(\w+)/g;

    return path.replaceAll(
      pathParamRegExp,
      (m: string, p1: string) => pathParams[p1] ?? m,
    );
  }

  private formatHeaders(auth?: ConnectionAuthModel, params?: ConnectionParams): Record<string, string> {
    const headers: Record<string, string> = {
      ...params?.headers,
    };

    if (auth?.token) {
      headers['Authorization'] = `Bearer ${auth.token.bearer}`;
    }

    if (auth?.customerNumber) {
      headers['customer-number'] = auth.customerNumber;
    }

    if (auth?.token?.userId) {
      headers['user-id'] = auth.token.userId;
    }

    // Identifies the calling app; agent tokens share one audience across apps.
    const applicationId: string | undefined = this.env.app().applicationId;

    if (applicationId) {
      headers['application-id'] = applicationId;
    }

    headers['platform-id'] = this.capacitorService.getPlatform();

    const language: string | undefined = this.cmsLanguageService.getRequestLanguage();

    if (language && !headers['accept-language']) {
      headers['accept-language'] = language;
    }

    return headers;
  }
}
